import { Check } from 'lucide-react'
import { Tooltip } from '@/components/ui/Tooltip'
import { useApproveWeek } from '@/lib/time-queries'
import type { WeekApprovalState } from '@/lib/types'
import { cn } from '@/lib/utils'

const GLYPH_TITLE: Record<WeekApprovalState, string> = {
  approved: 'Week afgesloten',
  open: 'Week nog niet afgesloten',
  partial: 'Deels afgesloten met de maand',
  changed: 'Gewijzigd na akkoord',
}

export function ApprovalGlyph({ state, className }: { state: WeekApprovalState; className?: string }) {
  if (state === 'approved') {
    return (
      <Tooltip content={GLYPH_TITLE.approved}>
        <span className={cn('inline-flex', className)}>
          <Check className="h-3.5 w-3.5 text-status-paid" aria-label={GLYPH_TITLE.approved} />
        </span>
      </Tooltip>
    )
  }

  // Deels afgesloten en gewijzigd-na-akkoord zijn allebei "half": de een is half
  // dicht, de ander half te vertrouwen. Ze delen de halve vulling maar niet de kleur,
  // anders zijn ze op een rij niet uit elkaar te houden.
  if (state === 'changed' || state === 'partial') {
    const rgb = state === 'partial' ? 'var(--status-paid-rgb)' : 'var(--status-approval-rgb)'
    return (
      <Tooltip content={GLYPH_TITLE[state]}>
        <span
          aria-label={GLYPH_TITLE[state]}
          className={cn('inline-block h-2.5 w-2.5 rounded-full', className)}
          style={{
            border: `1px solid rgb(${rgb})`,
            background: `linear-gradient(90deg, rgb(${rgb}) 50%, transparent 50%)`,
          }}
        />
      </Tooltip>
    )
  }

  return (
    <Tooltip content={GLYPH_TITLE.open}>
      <span
        aria-label={GLYPH_TITLE.open}
        className={cn('inline-block h-2.5 w-2.5 rounded-full border border-ink-faint/45', className)}
      />
    </Tooltip>
  )
}

interface ApproveButtonProps {
  weekId: string
  personId: string
  state: WeekApprovalState
}

export function TeamApproveButton({ weekId, personId, state }: ApproveButtonProps) {
  const approve = useApproveWeek(weekId, personId)
  if (state === 'approved') return null

  return (
    <button
      type="button"
      disabled={approve.isPending}
      onClick={(event) => {
        event.stopPropagation()
        approve.mutate(undefined)
      }}
      className="btn-ghost h-7 px-2 text-xs opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
    >
      {state === 'changed' ? 'Opnieuw' : state === 'partial' ? 'Rest afsluiten' : 'Sluit af'}
    </button>
  )
}
